import { z } from 'zod';

// ---------- POST /api/auth/login ----------

export const LoginRequestSchema = z.object({
  identificador: z.string(),
  clave: z.string(),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/**
 * Forma PLANA del token: la devuelven `/seleccionar-tenant` y `/verify-2fa`.
 * La rama `token_directo` de `/login` la anida en `tokenResponse` (ver
 * `LoginResponseTokenDirectoSchema`). La asimetría es intencional del
 * backend (`docs/backend/CLAUDE.md` §10.1; índice en `02_CONTRATOS.md` §1).
 * El helper `extractToken()` que normaliza ambas formas vive en
 * `@alter/api-client`, no acá.
 */
export const TokenResponseSchema = z.object({
  access_token: z.string(),
  tipo: z.string(),
  locale: z.string(),
  tenant_nombre: z.string(),
  usuario_nombre: z.string(),
});
export type TokenResponse = z.infer<typeof TokenResponseSchema>;

export const LoginTenantSchema = z.object({
  id: z.string(),
  nombre: z.string(),
});
export type LoginTenant = z.infer<typeof LoginTenantSchema>;

/** Discriminante de las 3 ramas de `/login`. Estricto — contrato cerrado. */
export const LOGIN_TIPOS = ['token_directo', 'seleccion_requerida', 'requiere_2fa'] as const;
export type LoginTipo = (typeof LOGIN_TIPOS)[number];
export const LoginTipoSchema = z.enum(LOGIN_TIPOS);

/** Respuesta A: acceso directo (único tenant, sin 2FA). */
export const LoginResponseTokenDirectoSchema = z.object({
  tipo: z.literal('token_directo'),
  tokenResponse: TokenResponseSchema,
});
export type LoginResponseTokenDirecto = z.infer<typeof LoginResponseTokenDirectoSchema>;

/** Respuesta B: el usuario pertenece a varios tenants y debe elegir. */
export const LoginResponseSeleccionRequeridaSchema = z.object({
  tipo: z.literal('seleccion_requerida'),
  selectorToken: z.string(),
  tenants: z.array(LoginTenantSchema),
});
export type LoginResponseSeleccionRequerida = z.infer<typeof LoginResponseSeleccionRequeridaSchema>;

/** Respuesta C: el usuario tiene 2FA activo y debe verificar. */
export const LoginResponseRequiere2FASchema = z.object({
  tipo: z.literal('requiere_2fa'),
  otpToken: z.string(),
});
export type LoginResponseRequiere2FA = z.infer<typeof LoginResponseRequiere2FASchema>;

/** Discriminada por `tipo`. Las 3 ramas de `/login`. */
export const LoginResponseSchema = z.discriminatedUnion('tipo', [
  LoginResponseTokenDirectoSchema,
  LoginResponseSeleccionRequeridaSchema,
  LoginResponseRequiere2FASchema,
]);
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

// ---------- POST /api/auth/seleccionar-tenant ----------

export const SeleccionarTenantRequestSchema = z.object({
  selectorToken: z.string(),
  tenantId: z.string(),
});
export type SeleccionarTenantRequest = z.infer<typeof SeleccionarTenantRequestSchema>;

/** Forma plana (sin wrapper `tokenResponse`). */
export const SeleccionarTenantResponseSchema = TokenResponseSchema;
export type SeleccionarTenantResponse = z.infer<typeof SeleccionarTenantResponseSchema>;

// ---------- POST /api/auth/verify-2fa ----------

export const Verify2FARequestSchema = z.object({
  otpToken: z.string(),
  codigo: z.string(),
});
export type Verify2FARequest = z.infer<typeof Verify2FARequestSchema>;

/** Forma plana (sin wrapper `tokenResponse`). */
export const Verify2FAResponseSchema = TokenResponseSchema;
export type Verify2FAResponse = z.infer<typeof Verify2FAResponseSchema>;
